<template>
  <div class="bmap-wrap">
    <Bmap @loaded="bmapLoaded" />
    <slot></slot>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';

import Bmap from '@/components/bmapGL';
import { defaultConfig } from './utils';

import { utilDataRangeIntensity as UtilDataRangeIntensity } from 'mapv';
import { View, IconLayer, PointLayer, PolygonLayer } from 'mapvgl';

import { stateMapMarker as smm } from '@/utils/dict';
import { timer } from '@/utils/utils';
import { isArray, isEmpty, isObject } from '@/utils/is';
import { getOptions, isOnline } from '@/utils/deviceUtils/state';
import { getTime } from '@/utils/date';
import bmapTheme from '@/utils/bmapTheme';
import { drawMultiGradientCircleCanvas } from '@/utils/canvas/gradient-circle';

function positionParser(position) {
  if (isArray(position)) return position;
  if (typeof position === 'string') return position.split(' ');
  return [116.418261, 39.921984];
}

function positionParser2(position) {
  const def = [116.418261, 39.921984, 0, 18.5, 90];

  let params = [];

  if (typeof position === 'string') {
    params = position.split(' ');
  } else if (isArray(position)) {
    params = position;
  }

  return def.map((e, i) => params[i] || def[i]);
}

export default {
  name: 'DeviceMap',
  props: {
    speed: {
      type: Number,
      default: 1
    },
    mapConfig: {
      type: [Object, null],
      default: () => ({})
    }
  },
  data() {
    return {
      sns: [],
      pointData: [],
      details: {},
      pause: 2000,
      max: 10,
      interval: null,
      isDestroyed: false,
      bmap: null,
      view: null,
      markerLayers: {},
      labelLayer: {},
      pointLayer: {},
      polygonLayer: null,
      infoWindow: null,
      setVisible: false,
      callbacks: [],
      canvasOverlay: null,
      gradientCircleLayers: {}
    };
  },
  computed: {
    ...mapState('socket', ['deviceSNMap']),
    config() {
      return Object.assign({}, defaultConfig, this.mapConfig, { max: this.max });
    },
    intensity() {
      return new UtilDataRangeIntensity(this.config);
    }
  },
  components: { Bmap },
  methods: {
    addMarker() {
      // 五角星
      var marker = new BMapGL.Marker(new BMapGL.Point(116.571449, 40.08898), {
        icon: new BMapGL.SVGSymbol(BMapGL.SymbolShapeType.BMap_Symbol_SHAPE_STAR, {
          fillColor: 'red',
          fillOpacity: 0.8,
          scale: 1,
          anchor: new BMapGL.Size(0, 5)
        })
      });

      this.bmap.addOverlay(marker);
    },
    updateCallback(fn) {
      this.callbacks.push(fn);
    },
    execCallback() {
      while (this.callbacks.length) {
        this.callbacks.shift()();
      }
    },
    radiusChange() {
      this.radiusChanged = true;
    },
    upBasicData(res) {
      if (!res) return;

      this.max = res.max;

      const deviceData = isArray(res.deviceData) ? res.deviceData : [];
      const pointData = deviceData;

      this.details = deviceData.reduce((map, e) => {
        map[e.sn] = e;
        return map;
      }, {});
      this.pointData = pointData;
    },
    setCenter(coordinate) {
      coordinate = positionParser(coordinate);
      const point = new BMapGL.Point(coordinate[0], coordinate[1]);
      this.bmap.centerAndZoom(point, 18);
    },
    setTilt(v) {
      this.bmap.setTilt(v);
    },
    animation(coordinate) {
      const [lng, lat, tilt, zoom, heading] = positionParser2(coordinate);

      this.heading = heading;

      // console.log(this.bmap);

      this.bmap.centerAndZoom(new BMapGL.Point(lng, lat), zoom, {
        heading,
        tilt,
        duration: 1500
      });
    },
    initMap() {
      const point = new BMapGL.Point(116.387456, 39.925406);
      this.bmap.centerAndZoom(point, 13);
      this.bmap.setMapStyleV2({ styleJson: bmapTheme.dark });
    },
    initMarkerLayer() {
      const status = ['离线', '正常', '预警', '报警'];
      status.forEach((e, i) => {
        this.markerLayers[e] = new IconLayer({
          width: 32,
          height: 32,
          offset: [0, -16],
          icon: smm[i - 1],
          enablePicked: true, // 是否可以拾取
          selectedIndex: -1, // 选中项
          // selectedColor: '#ff0000', // 选中项颜色
          autoSelect: false, // 根据鼠标位置来自动设置选中项
          renderOrder: 30,
          onClick: e => {
            // 点击事件
            if (!e.dataItem) return;
            const { data, geometry } = e.dataItem;
            const [lng, lat] = geometry.coordinates;
            this.makeWindow({ point: { lng, lat }, data });
          }
        });
        this.view.addLayer(this.markerLayers[e]);
      });
    },
    initPolygenLayer() {
      this.polygonLayer = new PolygonLayer({
        enablePicked: false, // 是否可以拾取
        selectedIndex: -1, // 选中数据项索引
        selectedColor: '#ff0000', // 选中项颜色
        autoSelect: false, // 根据鼠标位置来自动设置选中项
        renderOrder: 1,
        lineColor: 'rgba(250, 250, 25, 0.3)',
        lineJoin: 'miter',
        fillColor: 'rgba(25, 25, 250, 0)',
        lineWidth: 4
      });
      this.view.addLayer(this.polygonLayer);
    },
    setPolygonFill(p, color) {
      p.setOptions({ fillColor: color });
    },
    drawPolygon(data) {
      const geoJson = {
        geometry: {
          type: 'Polygon',
          coordinates: [data]
        },
        properties: {
          height: 0 // 多边形高度
        }
      };
      this.polygonLayer.setData([geoJson]);
    },
    getContent({ data, router }) {
      const { sn, state, runstate } = data || {};
      const { name, deviceType } = this.deviceSNMap[sn];
      const { t, c } = getOptions(+runstate, +state);
      const MyComponent = Vue.extend({
        template: `
          <div>
            <div>
              <span>设备号:</span>
              <span>${sn}</span>
            </div>
            <div>
              <span>设备名:</span>
              <span>${name}</span>
            </div>
            <div>
              <span>状态:</span>
              <span style="color: ${c};">${t}</span>
            </div>
            <div style="text-align: right">
              <el-button type="text" size="mini" @click="pushToData" :disabled="${
                t === '离线'
              }">监控页面</el-button>
            </div>
          </div>
        `,
        methods: {
          pushToData() {
            router.push({
              name: 'Realdata',
              params: {
                sn,
                deviceType
              }
            });
          }
        }
      });
      return new MyComponent().$mount().$el;
    },
    makeWindow({ point, data }) {
      const { sn } = data || {};
      if (!sn) return;
      const content = this.getContent({ data, router: this.$router });
      if (!this.infoWindow) {
        this.infoWindow = new BMapGL.InfoWindow(content, {
          width: 160,
          height: 110,
          offset: { width: 0, height: -32 }
        });
      } else {
        this.infoWindow.setContent(content);
      }
      this.bmap.openInfoWindow(this.infoWindow, point);
    },
    drawMarkers(data) {
      const details = Object.values(this.details);
      data = details;
      if (Array.isArray(this.sns) && this.sns.length && isObject(details)) {
        data = this.sns.map(e => this.details[e]);
      }
      const collection = { 离线: [], 正常: [], 预警: [], 报警: [] };
      data.forEach(e => {
        if (!e) return;
        const key = getOptions(+e.runstate, +e.state).t;
        const item = (collection[key] = collection[key] || []);
        item.push({
          geometry: {
            type: 'Point',
            coordinates: positionParser(e.coordinate)
          },
          data: {
            sn: e.sn,
            state: +e.state,
            runstate: +e.runstate
          }
        });
      });
      Object.keys(collection).forEach(state => {
        this.markerLayers[state]?.setData?.(collection[state]);
      });
    },
    initLabelLayer() {
      this.labelLayers = {};
      // this.labelLayer = new LabelLayer({
      //   textAlign: 'center',
      //   textColor: '#fc0',
      //   borderColor: '#666',
      //   backgroundColor: '#666',
      //   offset: [-30, 0],
      //   padding: [2, 5],
      //   borderRadius: 5,
      //   fontSize: 12,
      //   renderOrder: 50,
      //   lineHeight: 16,
      //   collides: false, // 是否开启碰撞检测, 数量较多时建议打开
      //   enablePicked: false
      // });
      // this.view.addLayer(this.labelLayer);
    },
    drawLabels() {
      let data = Object.values(this.details);
      const max = this.max;
      if (this.sns.length) {
        data = this.sns.map(e => this.details[e]);
      }

      data.forEach(e => {
        if (!e || !e.coordinate) return;

        if (!isOnline(+e.runstate)) {
          this.bmap.removeOverlay(this.labelLayers[e.sn]);
          delete this.labelLayers[e.sn];
          return;
        }

        let val = Math.min(max, e.data);
        val = isEmpty(val) ? 0 : val;
        val = Number(Number(val).toFixed(2));

        // console.log(val);

        if (!this.labelLayers[e.sn]) {
          const label = new BMapGL.Label(`${val}`, {
            position: new BMapGL.Point(...positionParser(e.coordinate)), // 指定文本标注所在的地理位置
            offset: new BMapGL.Size(0, -30) // 设置文本偏移量
          });

          this.labelLayers[e.sn] = label;

          label.setStyle({
            color: '#d2ae1e',
            background: '#666666',
            border: 'unset',
            borderRadius: '5px',
            padding: '3px 9px',
            fontSize: '14px',
            fontFamily: '微软雅黑',
            transform: 'translate(-50%, -34px)'
          });

          this.bmap.addOverlay(label);
        } else {
          this.labelLayers[e.sn].setContent(`${val}`);
          this.labelLayers[e.sn].setPosition(new BMapGL.Point(...positionParser(e.coordinate)));
        }
      });

      // const geoJson = data
      //   .map(e => {
      //     if (!e || !e.coordinate || !isOnline(+e.runstate)) return;
      //     let val = e.data > max ? max : e.data;
      //     val = val || 0;
      //     val = Number(Number(val).toFixed(2));
      //     return {
      //       geometry: {
      //         type: 'Point',
      //         coordinates: positionParser(e.coordinate)
      //       },
      //       properties: {
      //         text: `${val}`
      //       }
      //     };
      //   })
      //   .filter(Boolean);
      // this.labelLayer.setData(geoJson);
    },
    drawPoints() {
      // this.pointLayer.setOptions({
      //   size: this.config.size,
      //   renderOrder: 5,
      //   data: this.pointData
      // });

      const data = this.pointData.filter(e => e.state > 0 && e.runstate > 0);

      if (!isArray(data) || isEmpty(data)) {
        this.bmap.removeOverlay(this.canvasOverlay);
        this.canvasOverlay = null;
        return;
      }

      if (this.canvasOverlay) {
        this.bmap.removeOverlay(this.canvasOverlay);
        this.canvasOverlay = null;
      }

      const { canvas, bounds } = drawMultiGradientCircleCanvas({
        data: data.map(e => ({ coordinate: e.coordinate, value: e.data, radius: e.radius })),
        gap: 20,
        maxValue: this.max,
        gradient: this.mapConfig.gradient
      });

      var b = new BMapGL.Bounds(new BMapGL.Point(...bounds[0]), new BMapGL.Point(...bounds[1]));

      this.canvasOverlay = new BMapGL.GroundOverlay(b, {
        type: 'canvas',
        url: canvas,
        opacity: 0.9
      });

      this.bmap.addOverlay(this.canvasOverlay);
    },
    initPointLayer() {
      this.pointLayer = new PointLayer({
        size: 15,
        borderColor: 'rgba(0, 0, 0, 0)',
        unit: 'm',
        shape: 'square',
        renderOrder: 10,
        blend: 'lighter',
        color: data => {
          return this.intensity.getColor(data.properties.count);
        }
      });
      this.view.addLayer(this.pointLayer);
    },
    setPointLayer(layer, config) {
      layer?.setOptions?.(config);
    },
    initView() {
      this.view = new View({
        map: this.bmap
      });
    },
    init() {
      this.initMap();
      this.addMarker();
      this.initView();
      this.initPolygenLayer();
      // this.initPointLayer();
      this.initMarkerLayer();
      this.initLabelLayer();
      this.updateInterval();
    },
    checkOnline(data) {
      if (!isObject(data)) return;
      data = Object.values(data);
      const now = Date.now() - getTime('s') * 30;
      data.forEach(e => {
        if (e.updateTime < now) {
          e.runstate = 0;
        }
      });
    },
    bmapLoaded(map) {
      this.bmap = map;
      this.resetDetails();
      this.init();

      this.bmap.addEventListener('click', e => {
        console.log('点击坐标:', e.latlng);
      });
    },
    updateInterval(pause) {
      pause = pause || this.pause;
      clearInterval(this.interval);
      const cb = () => {
        if (this.isDestroyed) return;
        this.checkOnline(this.details);
        timer(this.drawPoints());
        timer(this.execCallback());
        timer(this.drawMarkers());
        timer(this.drawLabels());
        // timer(() => {
        //   this.setPolygonFill(this.polygonLayer, this.intensity.getColor((this.max || 10) * 0.05));
        // });
      };
      cb();
      this.interval = setInterval(cb, pause);
    },
    destroy() {
      clearInterval(this.interval);
      Object.values(this.markerLayers).forEach(layer => {
        layer.clear();
      });
      // this.labelLayer.clear();
      this.polygonLayer.clear();
      // this.pointLayer.clear();
      this.view.removeAllLayers();
      this.view.destroy();
    },
    showLabelChange() {
      this.drawLabels();
    },
    resetDetails() {
      this.$set(this, 'details', {});
    },
    reset() {
      this.destroy();
      this.init();
      this.resetDetails();
    }
  },
  beforeDestroy() {
    this.destroy();
    this.isDestroyed = true;
  }
};
</script>

<style lang="scss" scoped>
.bmap-wrap {
  width: 100%;
  height: calc(100% - 30px);
  position: relative;
}
</style>
